import React, { useId } from 'react';

export type LoaderSize = 'sm' | 'md' | 'lg';

interface AdminLoaderProps {
    message?: string;
    /** Keeps compatibility with existing callers while allowing contextual sizing. */
    fullScreen?: boolean;
    size?: LoaderSize;
    className?: string;
}

const sizeStyles: Record<LoaderSize, { frame: string; label: string; gap: string }> = {
    sm: { frame: 'h-10 w-10', label: 'text-[9px]', gap: 'mt-2' },
    md: { frame: 'h-14 w-14', label: 'text-[10px]', gap: 'mt-3' },
    lg: { frame: 'h-20 w-20', label: 'text-[11px]', gap: 'mt-4' },
};

/** Brand logo silhouette: outer quad + inner quad cut out with evenodd. */
const LOGO_PATH =
    'M45,52 L272,45 L275,270 L45,208 Z ' + // outer
    'M70,80 L240,78 L240,228 L68,197 Z';   // inner (hole)

/** Centerline of the frame, used only as the mask "track" for the chase animation. */
const TRACK_PATH = 'M57,65 L256,61 L257,249 L56,202 Z';

/** Loader animation styles, kept in this file so the component is self-contained. */
const LOADER_CSS = `
.deco-loader-chase {
    stroke-dasharray: 38 62; /* 38% of the frame lit, 62% dark */
    stroke-dashoffset: 0;
    animation: deco-loader-chase 1.4s linear infinite;
}

@keyframes deco-loader-chase {
    to { stroke-dashoffset: -100; }
}

@media (prefers-reduced-motion: reduce) {
    .deco-loader-chase {
        animation: none;
        stroke-dasharray: 100 0; /* show the full logo, no motion */
    }
}
`;

/** Shared branded loader for route, page, panel, and table loading states. */
export const UniversalLoader: React.FC<AdminLoaderProps> = ({
    message = 'Loading data...',
    fullScreen = true,
    size = fullScreen ? 'lg' : 'md',
    className = '',
}) => {
    const sizing = sizeStyles[size];
    const maskId = useId().replace(/:/g, '');

    return (
        <div
            role="status"
            aria-live="polite"
            className={`${fullScreen
                ? 'fixed inset-0 z-50 min-h-screen bg-[#f7f8fa]/90 backdrop-blur-sm'
                : `w-full ${size === 'sm' ? 'min-h-[120px] py-6' : 'min-h-[180px] py-10'}`
            } flex flex-col items-center justify-center ${className}`}
        >
            <style>{LOADER_CSS}</style>

            <div className={`relative ${sizing.frame}`} aria-hidden="true">
                <svg viewBox="30 30 260 255" className="h-full w-full" fill="none">
                    <defs>
                        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="320" height="320">
                            <path
                                d={TRACK_PATH}
                                stroke="#fff"
                                strokeWidth="64"
                                strokeLinejoin="miter"
                                strokeMiterlimit="10"
                                pathLength="100"
                                className="deco-loader-chase"
                            />
                        </mask>
                    </defs>

                    {/* Dim base logo */}
                    <path d={LOGO_PATH} fill="#B08D55" fillOpacity="0.18" fillRule="evenodd" />

                    {/* Bright logo, revealed only where the mask segment is */}
                    <path
                        d={LOGO_PATH}
                        fill="#B08D55"
                        fillRule="evenodd"
                        mask={`url(#${maskId})`}
                    />
                </svg>
            </div>

            {message && (
                <p className={`${sizing.gap} ${sizing.label} font-bold uppercase tracking-[0.18em] text-[#a9854e] animate-pulse`}>
                    {message}
                </p>
            )}
            <span className="sr-only">Loading</span>
        </div>
    );
};

// Backward-compatible names for existing admin imports.
export const AdminLoader = UniversalLoader;
export default UniversalLoader;